import axios from 'axios';
import { store } from '../store';
import { selectApiBaseUrl } from '../store/slices/configSlice';

const attachAuth = (config) => {
  config.baseURL = selectApiBaseUrl(store.getState());
  const token = localStorage.getItem('shm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
};

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  // OTP login — restore when email verification is re-enabled
  // '/auth/request-otp',
  // '/auth/verify-otp',
  '/auth/set-password',
  '/auth/forgot-password',
  '/auth/forgot-password/reset',
];

const isPublicAuthRequest = (url = '') =>
  PUBLIC_AUTH_PATHS.some((path) => url.includes(path));

const handleAuthError = (err) => {
  if (err.response?.status === 401 && !isPublicAuthRequest(err.config?.url)) {
    localStorage.removeItem('shm_token');
    localStorage.removeItem('shm_user');
    window.location.href = '/login';
  }
  const error = new Error(err.response?.data?.message || err.message || 'API Error');
  error.status = err.response?.status;
  if (err.response?.data?.attemptsRemaining !== undefined) {
    error.attemptsRemaining = err.response.data.attemptsRemaining;
  }
  if (err.response?.data?.lockedUntil) {
    error.lockedUntil = err.response.data.lockedUntil;
  }
  return Promise.reject(error);
};

const instance = axios.create({
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/** Same auth headers; keeps full Axios response (needed for HTTP status codes). */
const statusInstance = axios.create({
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

instance.interceptors.request.use(attachAuth);
statusInstance.interceptors.request.use(attachAuth);

instance.interceptors.response.use((res) => res.data, handleAuthError);
statusInstance.interceptors.response.use((res) => res, handleAuthError);

const api = {
  get: (url, params, config = {}) => instance.get(url, { params, ...config }),
  /** Returns { status, data } so callers can handle 202 Accepted */
  getWithStatus: (url, params, config = {}) =>
    statusInstance.get(url, { params, ...config }).then((res) => ({
      status: res.status,
      data: res.data,
    })),
  post: (url, body, config = {}) => instance.post(url, body, config),
  put: (url, body, config = {}) => instance.put(url, body, config),
  delete: (url) => instance.delete(url),
  download: (url, params) =>
    instance.get(url, { params, responseType: 'blob' }),
  upload: (url, formData) =>
    instance.post(url, formData, {
      // Do not force multipart Content-Type — browser/axios must set boundary
      headers: { 'Content-Type': undefined },
      transformRequest: [(data, headers) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type');
          } else if (headers) {
            delete headers['Content-Type'];
            delete headers['content-type'];
          }
        }
        return data;
      }],
    }),
};

export default api;
